import { UploadAudioDocument } from "@/generated/graphql";
import { useAppSelector } from "@/store/hooks";
import { MediaUsageContextSchema } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import toast from "react-hot-toast";
import type { MediaInput } from "./handlers/types";

/**
 * Handles uploading a local audio file and calling the provided handler
 * with the result — keeping local file drops consistent with dnd-from-media drops.
 *
 * Usage:
 *   const { handleFileDrop, isUploading } = useLocalAudioUpload(
 *     (media, containerId) => handlers.addAudio(media, containerId)
 *   );
 */
export function useLocalAudioUpload(onUploaded: (media: MediaInput, containerId?: string) => void) {
  const [isUploading, setIsUploading] = useState(false);
  // Tags the Media House copy by the slide being edited. Null outside the slide editor
  // (the lesson generator's draft editor), where the server falls back.
  const slideTemplate = useAppSelector((state) => state.slideEditor.present.slide?.template);
  const [uploadAudioMutation] = useMutation(UploadAudioDocument);

  const handleFileDrop = async (file: File, containerId?: string) => {
    if (!file.type.startsWith("audio/")) {
      toast.error("Only audio files are supported");
      return;
    }

    setIsUploading(true);
    toast.loading("Uploading audio...", { id: "audioUploadToast" });

    try {
      const upload = await uploadAudioMutation({
        variables: {
          file,
          folder: "media/audio",
          usageContext: MediaUsageContextSchema.Values.slideEditor,
          slideTemplate,
        },
      });

      if (!upload.data || upload.error) throw new Error();

      const { id, url, filename } = upload.data.uploadAudio;
      onUploaded({ id, src: url, filename }, containerId);
      toast.success("Audio uploaded", { id: "audioUploadToast" });
    } catch (_error) {
      toast.error("Failed to upload audio", { id: "audioUploadToast" });
    } finally {
      setIsUploading(false);
    }
  };

  return { handleFileDrop, isUploading };
}
